import {
  Point2D,
  CourbeBezier,
  LigneDroite,
  MotifPersonnalise,
  OutilDessin,
  EtatSelection
} from '../types';

/**
 * Service de gestion du canvas 2D pour le dessin de motifs personnalisés
 * Permet aux utilisateurs de créer leurs propres motifs de traverses
 */
export class Canvas2DService {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private isDrawing: boolean = false;
  private currentPath: Point2D[] = [];
  private paths: Point2D[][] = [];
  private bezierCurves: CourbeBezier[] = [];
  private lignesDroites: LigneDroite[] = [];
  private currentTool: OutilDessin = OutilDessin.CRAYON;
  private etatSelection: EtatSelection = {};
  private ligneEnCours: LigneDroite | null = null;
  private bezierEnCours: CourbeBezier | null = null;
  private etapeBezier: number = 0; // 0: début, 1: fin, 2: contrôle1, 3: contrôle2
  private gridSize: number = 10;
  private showGrid: boolean = true;
  private scale: number = 1;
  private offset: Point2D = { x: 0, y: 0 };
  private isDragging: boolean = false;
  private dragStart: Point2D = { x: 0, y: 0 };
  private nextId: number = 1;
  private accrocheActive: boolean = true;
  private rayonAccroche: number = 20;
  private selectedPathIndex: number = -1; // Index du chemin de crayon sélectionné

  // Couleurs et styles
  private readonly GRID_COLOR = '#e0e0e0';
  private readonly CRAYON_COLOR = '#2196F3';
  private readonly LIGNE_COLOR = '#4CAF50';
  private readonly BEZIER_COLOR = '#FF5722';
  private readonly CONTROL_POINT_COLOR = '#FFC107';
  private readonly SELECTED_COLOR = '#E91E63';
  private readonly HANDLE_COLOR = '#9C27B0';
  private readonly BACKGROUND_COLOR = '#ffffff';
  private readonly HANDLE_SIZE = 6;
  private readonly SELECTION_TOLERANCE = 15;
  private readonly ACCROCHE_COLOR = '#00BCD4';
  private readonly ACCROCHE_SIZE = 8;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Impossible d\'obtenir le contexte 2D du canvas');
    }
    this.ctx = context;
    this.initialiserEvenements();
    this.redessiner();
  }

  /**
   * Initialise les événements de souris et tactiles
   */
  private initialiserEvenements(): void {
    // Événements de souris
    this.canvas.addEventListener('mousedown', this.onMouseDown.bind(this));
    this.canvas.addEventListener('mousemove', this.onMouseMove.bind(this));
    this.canvas.addEventListener('mouseup', this.onMouseUp.bind(this));
    this.canvas.addEventListener('wheel', this.onWheel.bind(this));

    // Événements tactiles pour mobile
    this.canvas.addEventListener('touchstart', this.onTouchStart.bind(this));
    this.canvas.addEventListener('touchmove', this.onTouchMove.bind(this));
    this.canvas.addEventListener('touchend', this.onTouchEnd.bind(this));

    // Événements clavier
    this.canvas.addEventListener('keydown', this.onKeyDown.bind(this));
    
    // Rendre le canvas focusable pour recevoir les événements clavier
    this.canvas.tabIndex = 0;

    // Empêcher le comportement par défaut
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  /**
   * Gestion du clic de souris
   */
  private onMouseDown(event: MouseEvent): void {
    const point = this.getPointFromEvent(event);
    
    switch (this.currentTool) {
      case OutilDessin.CRAYON:
        this.startDrawing(point);
        break;
      case OutilDessin.LIGNE:
        this.startLigneDroite(point);
        break;
      case OutilDessin.BEZIER:
        this.gererClicBezier(point);
        break;
      case OutilDessin.SELECTION:
        this.gererSelection(point);
        break;
    }
  }

  /**
   * Gestion du mouvement de souris
   */
  private onMouseMove(event: MouseEvent): void {
    const point = this.getPointFromEvent(event);
    
    if (this.isDragging && this.currentTool === OutilDessin.SELECTION) {
      this.gererDeplacement(point);
    } else if (this.isDrawing && this.currentTool === OutilDessin.CRAYON) {
      this.continueDrawing(point);
    } else if (this.ligneEnCours && this.currentTool === OutilDessin.LIGNE) {
      this.mettreAJourLigneEnCours(point);
    } else if (this.bezierEnCours && this.currentTool === OutilDessin.BEZIER) {
      this.mettreAJourBezierEnCours(point);
    }
    
    this.redessiner();
  }

  /**
   * Gestion du relâchement de souris
   */
  private onMouseUp(event: MouseEvent): void {
    if (this.currentTool === OutilDessin.CRAYON) {
      this.stopDrawing();
    } else if (this.currentTool === OutilDessin.LIGNE && this.ligneEnCours) {
      this.terminerLigneDroite();
    } else if (this.currentTool === OutilDessin.SELECTION) {
      this.isDragging = false;
    }
  }

  /**
   * Gestion du zoom avec la molette
   */
  private onWheel(event: WheelEvent): void {
    event.preventDefault();
    const delta = event.deltaY > 0 ? 0.9 : 1.1;
    this.scale *= delta;
    this.scale = Math.max(0.1, Math.min(5, this.scale));
    this.redessiner();
  }

  /**
   * Gestion du toucher (mobile)
   */
  private onTouchStart(event: TouchEvent): void {
    event.preventDefault();
    if (event.touches.length === 1) {
      const point = this.getPointFromTouch(event.touches[0]);
      this.startDrawing(point);
    }
  }

  /**
   * Gestion du mouvement tactile
   */
  private onTouchMove(event: TouchEvent): void {
    event.preventDefault();
    if (!this.isDrawing || event.touches.length !== 1) return;
    const point = this.getPointFromTouch(event.touches[0]);
    this.continueDrawing(point);
  }

  /**
   * Gestion de la fin du toucher
   */
  private onTouchEnd(event: TouchEvent): void {
    event.preventDefault();
    this.stopDrawing();
  }

  /**
   * Gestion des événements clavier
   */
  private onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Delete':
      case 'Backspace':
        event.preventDefault();
        this.supprimerSelection();
        break;
    }
  }

  /**
   * Obtient les coordonnées à partir d'un événement de souris
   */
  private getPointFromEvent(event: MouseEvent): Point2D {
    const rect = this.canvas.getBoundingClientRect();
    const point = {
      x: (event.clientX - rect.left - this.offset.x) / this.scale,
      y: (event.clientY - rect.top - this.offset.y) / this.scale
    };
    return this.appliquerAccroche(point);
  }

  /**
   * Obtient les coordonnées à partir d'un toucher
   */
  private getPointFromTouch(touch: Touch): Point2D {
    const rect = this.canvas.getBoundingClientRect();
    const point = {
      x: (touch.clientX - rect.left - this.offset.x) / this.scale,
      y: (touch.clientY - rect.top - this.offset.y) / this.scale
    };
    return this.appliquerAccroche(point);
  }

  /**
   * Commence le dessin
   */
  private startDrawing(point: Point2D): void {
    this.isDrawing = true;
    this.currentPath = [point];
  }

  /**
   * Continue le dessin avec un nouveau point (outil crayon)
   */
  private continueDrawing(point: Point2D): void {
    if (this.isDrawing) {
      this.currentPath.push(point);
      this.redessiner();
    }
  }

  /**
   * Commence une ligne droite
   */
  private startLigneDroite(point: Point2D): void {
    this.ligneEnCours = {
      id: `ligne_${this.nextId++}`,
      debut: point,
      fin: point
    };
  }

  /**
   * Met à jour la ligne en cours
   */
  private mettreAJourLigneEnCours(point: Point2D): void {
    if (this.ligneEnCours) {
      this.ligneEnCours.fin = point;
    }
  }

  /**
   * Termine la ligne droite
   */
  private terminerLigneDroite(): void {
    if (this.ligneEnCours) {
      this.lignesDroites.push(this.ligneEnCours);
      this.ligneEnCours = null;
    }
  }

  /**
   * Gère les clics pour l'outil Bézier
   */
  private gererClicBezier(point: Point2D): void {
    if (this.etapeBezier === 0) {
      // Premier clic : point de début
      this.bezierEnCours = {
        id: `bezier_${this.nextId++}`,
        debut: point,
        fin: point,
        controle1: point,
        controle2: point
      };
      this.etapeBezier = 1;
    } else if (this.etapeBezier === 1) {
      // Deuxième clic : point de fin
      if (this.bezierEnCours) {
        this.bezierEnCours.fin = point;
        // Initialiser les points de contrôle
        const dx = point.x - this.bezierEnCours.debut.x;
        const dy = point.y - this.bezierEnCours.debut.y;
        this.bezierEnCours.controle1 = {
          x: this.bezierEnCours.debut.x + dx * 0.3,
          y: this.bezierEnCours.debut.y + dy * 0.3
        };
        this.bezierEnCours.controle2 = {
          x: this.bezierEnCours.fin.x - dx * 0.3,
          y: this.bezierEnCours.fin.y - dy * 0.3
        };
        this.etapeBezier = 2;
      }
    } else if (this.etapeBezier === 2) {
      // Troisième clic : premier point de contrôle
      if (this.bezierEnCours) {
        this.bezierEnCours.controle1 = point;
        this.etapeBezier = 3;
      }
    } else if (this.etapeBezier === 3) {
      // Quatrième clic : deuxième point de contrôle et fin
      if (this.bezierEnCours) {
        this.bezierEnCours.controle2 = point;
        this.bezierCurves.push(this.bezierEnCours);
        this.bezierEnCours = null;
        this.etapeBezier = 0;
      }
    }
  }

  /**
   * Met à jour la courbe de Bézier en cours
   */
  private mettreAJourBezierEnCours(point: Point2D): void {
    if (this.bezierEnCours) {
      if (this.etapeBezier === 1) {
        this.bezierEnCours.fin = point;
        // Mettre à jour les points de contrôle automatiquement
        const dx = point.x - this.bezierEnCours.debut.x;
        const dy = point.y - this.bezierEnCours.debut.y;
        this.bezierEnCours.controle1 = {
          x: this.bezierEnCours.debut.x + dx * 0.3,
          y: this.bezierEnCours.debut.y + dy * 0.3
        };
        this.bezierEnCours.controle2 = {
          x: this.bezierEnCours.fin.x - dx * 0.3,
          y: this.bezierEnCours.fin.y - dy * 0.3
        };
      } else if (this.etapeBezier === 2) {
        this.bezierEnCours.controle1 = point;
      } else if (this.etapeBezier === 3) {
        this.bezierEnCours.controle2 = point;
      }
    }
  }

  /**
   * Gère la sélection d'éléments
   */
  private gererSelection(point: Point2D): void {
    // Désélectionner tout d'abord
    this.deselectionnerTout();
    
    // Chercher un élément à sélectionner
    const elementSelectionne = this.trouverElementAuPoint(point);
    
    if (elementSelectionne) {
      this.etatSelection = elementSelectionne;
      this.marquerCommeSelectionne(elementSelectionne);
      this.isDragging = true;
      this.dragStart = point;
    }
  }

  /**
   * Gère le déplacement d'éléments sélectionnés
   */
  private gererDeplacement(point: Point2D): void {
    if (!this.etatSelection.elementSelectionne && this.selectedPathIndex === -1) return;
    
    const dx = point.x - this.dragStart.x;
    const dy = point.y - this.dragStart.y;
    
    // Gestion du déplacement des chemins de crayon
    if (this.selectedPathIndex !== -1) {
      const path = this.paths[this.selectedPathIndex];
      if (path) {
        path.forEach(point => {
          point.x += dx;
          point.y += dy;
        });
      }
    }
    
    if (this.etatSelection.typeElement === 'ligne') {
      const ligne = this.lignesDroites.find(l => l.id === this.etatSelection.elementSelectionne);
      if (ligne) {
        if (this.etatSelection.poigneeActive === 'debut') {
          ligne.debut.x += dx;
          ligne.debut.y += dy;
        } else if (this.etatSelection.poigneeActive === 'fin') {
          ligne.fin.x += dx;
          ligne.fin.y += dy;
        } else {
          // Déplacer toute la ligne
          ligne.debut.x += dx;
          ligne.debut.y += dy;
          ligne.fin.x += dx;
          ligne.fin.y += dy;
        }
      }
    } else if (this.etatSelection.typeElement === 'bezier') {
      const courbe = this.bezierCurves.find(c => c.id === this.etatSelection.elementSelectionne);
      if (courbe) {
        if (this.etatSelection.poigneeActive === 'debut') {
          courbe.debut.x += dx;
          courbe.debut.y += dy;
        } else if (this.etatSelection.poigneeActive === 'fin') {
          courbe.fin.x += dx;
          courbe.fin.y += dy;
        } else if (this.etatSelection.poigneeActive === 'controle1') {
          courbe.controle1.x += dx;
          courbe.controle1.y += dy;
        } else if (this.etatSelection.poigneeActive === 'controle2') {
          courbe.controle2.x += dx;
          courbe.controle2.y += dy;
        } else {
          // Déplacer toute la courbe
          courbe.debut.x += dx;
          courbe.debut.y += dy;
          courbe.fin.x += dx;
          courbe.fin.y += dy;
          courbe.controle1.x += dx;
          courbe.controle1.y += dy;
          courbe.controle2.x += dx;
          courbe.controle2.y += dy;
        }
      }
    }
    
    this.dragStart = point;
  }

  /**
   * Arrête le dessin
   */
  private stopDrawing(): void {
    if (this.isDrawing && this.currentPath.length > 1) {
      // Toujours stocker le chemin original, le lissage sera appliqué à l'affichage
      this.paths.push([...this.currentPath]);
    }
    this.isDrawing = false;
    this.currentPath = [];
    this.redessiner();
  }

  /**
   * Convertit une ligne droite en série de points pour le lissage
   */
  private convertirLigneEnPoints(debut: Point2D, fin: Point2D): Point2D[] {
    const points: Point2D[] = [];
    const nombrePoints = Math.max(3, Math.floor(this.distancePoint(debut, fin) / 10));
    
    for (let i = 0; i <= nombrePoints; i++) {
      const t = i / nombrePoints;
      points.push({
        x: debut.x + (fin.x - debut.x) * t,
        y: debut.y + (fin.y - debut.y) * t
      });
    }
    
    return points;
  }

  /**
   * Convertit une courbe de Bézier en série de points pour le lissage
   */
  private convertirBezierEnPoints(curve: CourbeBezier): Point2D[] {
    const points: Point2D[] = [];
    const nombrePoints = 20; // Nombre de points pour échantillonner la courbe
    
    for (let i = 0; i <= nombrePoints; i++) {
      const t = i / nombrePoints;
      const point = this.calculerPointBezier(curve, t);
      points.push(point);
    }
    
    return points;
  }

  /**
   * Lisse un chemin de points en arrondissant les angles aigus
   */


  /**
   * Calcule un point sur une courbe de Bézier quadratique
   */
  private calculerPointBezierQuadratique(p0: Point2D, p1: Point2D, p2: Point2D, t: number): Point2D {
    const mt = 1 - t;
    const mt2 = mt * mt;
    const t2 = t * t;
    
    return {
      x: mt2 * p0.x + 2 * mt * t * p1.x + t2 * p2.x,
      y: mt2 * p0.y + 2 * mt * t * p1.y + t2 * p2.y
    };
  }

  /**
   * Trouve un élément au point donné
   */
  private trouverElementAuPoint(point: Point2D): EtatSelection | null {
    // Vérifier les lignes droites
    for (const ligne of this.lignesDroites) {
      // Vérifier les poignées
      if (this.distancePoint(point, ligne.debut) <= this.SELECTION_TOLERANCE) {
        return { elementSelectionne: ligne.id, typeElement: 'ligne', poigneeActive: 'debut' };
      }
      if (this.distancePoint(point, ligne.fin) <= this.SELECTION_TOLERANCE) {
        return { elementSelectionne: ligne.id, typeElement: 'ligne', poigneeActive: 'fin' };
      }
      // Vérifier la ligne elle-même
      if (this.distancePointLigne(point, ligne.debut, ligne.fin) <= this.SELECTION_TOLERANCE) {
        return { elementSelectionne: ligne.id, typeElement: 'ligne' };
      }
    }
    
    // Vérifier les courbes de Bézier
    for (const courbe of this.bezierCurves) {
      // Vérifier les poignées
      if (this.distancePoint(point, courbe.debut) <= this.SELECTION_TOLERANCE) {
        return { elementSelectionne: courbe.id, typeElement: 'bezier', poigneeActive: 'debut' };
      }
      if (this.distancePoint(point, courbe.fin) <= this.SELECTION_TOLERANCE) {
        return { elementSelectionne: courbe.id, typeElement: 'bezier', poigneeActive: 'fin' };
      }
      if (this.distancePoint(point, courbe.controle1) <= this.SELECTION_TOLERANCE) {
        return { elementSelectionne: courbe.id, typeElement: 'bezier', poigneeActive: 'controle1' };
      }
      if (this.distancePoint(point, courbe.controle2) <= this.SELECTION_TOLERANCE) {
        return { elementSelectionne: courbe.id, typeElement: 'bezier', poigneeActive: 'controle2' };
      }
      // Vérifier la courbe elle-même
      if (this.distancePointCourbeBezier(point, courbe) <= this.SELECTION_TOLERANCE) {
        return { elementSelectionne: courbe.id, typeElement: 'bezier' };
      }
    }
    
    // Vérifier les chemins de crayon
    for (let i = 0; i < this.paths.length; i++) {
      const path = this.paths[i];
      if (path.length < 2) continue;
      
      // Vérifier chaque segment du chemin
      for (let j = 0; j < path.length - 1; j++) {
        const distance = this.distancePointLigne(point, path[j], path[j + 1]);
        if (distance <= this.SELECTION_TOLERANCE) {
          return { elementSelectionne: `path_${i}`, typeElement: 'crayon' };
        }
      }
    }
    
    return null;
  }

  /**
   * Calcule la distance entre deux points
   */
  private distancePoint(p1: Point2D, p2: Point2D): number {
    const dx = p1.x - p2.x;
    const dy = p1.y - p2.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Calcule la distance d'un point à une ligne
   */
  private distancePointLigne(point: Point2D, debut: Point2D, fin: Point2D): number {
    const A = point.x - debut.x;
    const B = point.y - debut.y;
    const C = fin.x - debut.x;
    const D = fin.y - debut.y;
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    
    if (lenSq === 0) return this.distancePoint(point, debut);
    
    let param = dot / lenSq;
    
    let xx, yy;
    
    if (param < 0) {
      xx = debut.x;
      yy = debut.y;
    } else if (param > 1) {
      xx = fin.x;
      yy = fin.y;
    } else {
      xx = debut.x + param * C;
      yy = debut.y + param * D;
    }
    
    const dx = point.x - xx;
    const dy = point.y - yy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Calcule la distance d'un point à une courbe de Bézier
   * Utilise une approximation en échantillonnant la courbe
   */
  private distancePointCourbeBezier(point: Point2D, courbe: CourbeBezier): number {
    let minDistance = Infinity;
    const samples = 50; // Nombre d'échantillons pour approximer la courbe
    
    for (let i = 0; i <= samples; i++) {
      const t = i / samples;
      const pointCourbe = this.calculerPointBezier(courbe, t);
      const distance = this.distancePoint(point, pointCourbe);
      minDistance = Math.min(minDistance, distance);
    }
    
    return minDistance;
  }

  /**
   * Calcule un point sur la courbe de Bézier pour un paramètre t donné (0 <= t <= 1)
   */
  private calculerPointBezier(courbe: CourbeBezier, t: number): Point2D {
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;
    const t2 = t * t;
    const t3 = t2 * t;
    
    return {
      x: mt3 * courbe.debut.x + 3 * mt2 * t * courbe.controle1.x + 3 * mt * t2 * courbe.controle2.x + t3 * courbe.fin.x,
      y: mt3 * courbe.debut.y + 3 * mt2 * t * courbe.controle1.y + 3 * mt * t2 * courbe.controle2.y + t3 * courbe.fin.y
    };
  }

  /**
   * Trouve le point d'accroche magnétique le plus proche (extrémités des lignes uniquement)
   */
  private trouverPointAccroche(point: Point2D): Point2D | null {
    if (!this.accrocheActive) return null;
    
    let pointLePlusProche: Point2D | null = null;
    let distanceMin = this.rayonAccroche;
    
    // Points des chemins de crayon (premier et dernier point de chaque chemin)
    for (const path of this.paths) {
      if (path.length > 0) {
        // Premier point du chemin
        const distanceDebut = this.distancePoint(point, path[0]);
        if (distanceDebut < distanceMin) {
          distanceMin = distanceDebut;
          pointLePlusProche = path[0];
        }
        
        // Dernier point du chemin (si différent du premier)
        if (path.length > 1) {
          const dernierPoint = path[path.length - 1];
          const distanceFin = this.distancePoint(point, dernierPoint);
          if (distanceFin < distanceMin) {
            distanceMin = distanceFin;
            pointLePlusProche = dernierPoint;
          }
        }
      }
    }
    
    // Points des lignes droites
    for (const ligne of this.lignesDroites) {
      const distanceDebut = this.distancePoint(point, ligne.debut);
      const distanceFin = this.distancePoint(point, ligne.fin);
      
      if (distanceDebut < distanceMin) {
        distanceMin = distanceDebut;
        pointLePlusProche = ligne.debut;
      }
      
      if (distanceFin < distanceMin) {
        distanceMin = distanceFin;
        pointLePlusProche = ligne.fin;
      }
    }
    
    // Points des courbes de Bézier
    for (const courbe of this.bezierCurves) {
      const points = [courbe.debut, courbe.fin, courbe.controle1, courbe.controle2];
      
      for (const pointCourbe of points) {
        const distance = this.distancePoint(point, pointCourbe);
        if (distance < distanceMin) {
          distanceMin = distance;
          pointLePlusProche = pointCourbe;
        }
      }
    }
    
    return pointLePlusProche;
  }

  /**
   * Applique l'accroche magnétique à un point
   */
  private appliquerAccroche(point: Point2D): Point2D {
    const pointAccroche = this.trouverPointAccroche(point);
    return pointAccroche || point;
  }

  /**
   * Désélectionne tous les éléments
   */
  private deselectionnerTout(): void {
    this.lignesDroites.forEach(ligne => ligne.selected = false);
    this.bezierCurves.forEach(courbe => courbe.selected = false);
    this.selectedPathIndex = -1; // Désélectionner le chemin de crayon
    this.etatSelection = {};
  }

  /**
   * Marque un élément comme sélectionné
   */
  private marquerCommeSelectionne(selection: EtatSelection): void {
    if (selection.typeElement === 'ligne') {
      const ligne = this.lignesDroites.find(l => l.id === selection.elementSelectionne);
      if (ligne) ligne.selected = true;
    } else if (selection.typeElement === 'bezier') {
      const courbe = this.bezierCurves.find(c => c.id === selection.elementSelectionne);
      if (courbe) courbe.selected = true;
    } else if (selection.typeElement === 'crayon') {
      // Pour les chemins de crayon, on stocke l'index sélectionné
      const pathIndex = parseInt(selection.elementSelectionne!.replace('path_', ''));
      if (pathIndex >= 0 && pathIndex < this.paths.length) {
        // Marquer le chemin comme sélectionné en stockant l'index
        this.selectedPathIndex = pathIndex;
      }
    }
  }

  /**
   * Redessine tout le canvas
   */
  private redessiner(): void {
    // Effacer le canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    
    // Fond blanc
    this.ctx.fillStyle = this.BACKGROUND_COLOR;
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Sauvegarder l'état du contexte
    this.ctx.save();
    
    // Appliquer la transformation
    this.ctx.translate(this.offset.x, this.offset.y);
    this.ctx.scale(this.scale, this.scale);

    // Dessiner la grille si activée
    if (this.showGrid) {
      this.dessinerGrille();
    }

    // Dessiner tous les chemins (crayon)
    this.dessinerChemins();

    // Dessiner les lignes droites
    this.dessinerLignesDroites();

    // Dessiner les courbes de Bézier
    this.dessinerCourbesBezier();

    // Dessiner les éléments en cours
    this.dessinerElementsEnCours();

    // Dessiner les poignées de sélection
    this.dessinerPoigneesSelection();

    // Dessiner les indicateurs d'accroche
    this.dessinerIndicateursAccroche();

    // Restaurer l'état du contexte
    this.ctx.restore();
  }

  /**
   * Dessine la grille
   */
  private dessinerGrille(): void {
    this.ctx.strokeStyle = this.GRID_COLOR;
    this.ctx.lineWidth = 1 / this.scale;
    this.ctx.beginPath();

    const startX = Math.floor(-this.offset.x / this.scale / this.gridSize) * this.gridSize;
    const endX = Math.ceil((this.canvas.width - this.offset.x) / this.scale / this.gridSize) * this.gridSize;
    const startY = Math.floor(-this.offset.y / this.scale / this.gridSize) * this.gridSize;
    const endY = Math.ceil((this.canvas.height - this.offset.y) / this.scale / this.gridSize) * this.gridSize;

    // Lignes verticales
    for (let x = startX; x <= endX; x += this.gridSize) {
      this.ctx.moveTo(x, startY);
      this.ctx.lineTo(x, endY);
    }

    // Lignes horizontales
    for (let y = startY; y <= endY; y += this.gridSize) {
      this.ctx.moveTo(startX, y);
      this.ctx.lineTo(endX, y);
    }

    this.ctx.stroke();
  }

  /**
   * Dessine tous les chemins (crayon)
   */
  private dessinerChemins(): void {
    this.ctx.lineWidth = 2 / this.scale;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';

    this.paths.forEach((path, index) => {
      if (path.length < 2) return;
      
      // Utiliser une couleur différente si le chemin est sélectionné
      this.ctx.strokeStyle = (index === this.selectedPathIndex) ? this.SELECTED_COLOR : this.CRAYON_COLOR;
      
      this.ctx.beginPath();
      this.ctx.moveTo(path[0].x, path[0].y);
      
      for (let i = 1; i < path.length; i++) {
        this.ctx.lineTo(path[i].x, path[i].y);
      }
      
      this.ctx.stroke();
    });
  }

  /**
   * Dessine toutes les lignes droites
   */
  private dessinerLignesDroites(): void {
    this.ctx.lineWidth = 2 / this.scale;
    this.ctx.lineCap = 'round';

    this.lignesDroites.forEach(ligne => {
      this.ctx.strokeStyle = ligne.selected ? this.SELECTED_COLOR : this.LIGNE_COLOR;
      this.ctx.beginPath();
      
      this.ctx.moveTo(ligne.debut.x, ligne.debut.y);
      this.ctx.lineTo(ligne.fin.x, ligne.fin.y);
      
      this.ctx.stroke();
    });
  }

  /**
   * Dessine les courbes de Bézier
   */
  private dessinerCourbesBezier(): void {
    this.ctx.lineWidth = 2 / this.scale;

    this.bezierCurves.forEach(curve => {
      this.ctx.strokeStyle = curve.selected ? this.SELECTED_COLOR : this.BEZIER_COLOR;
      this.ctx.beginPath();
      
      this.ctx.moveTo(curve.debut.x, curve.debut.y);
      this.ctx.bezierCurveTo(
        curve.controle1.x, curve.controle1.y,
        curve.controle2.x, curve.controle2.y,
        curve.fin.x, curve.fin.y
      );
      
      this.ctx.stroke();

      // Dessiner les lignes de contrôle si sélectionné
      if (curve.selected) {
        this.ctx.strokeStyle = this.CONTROL_POINT_COLOR;
        this.ctx.lineWidth = 1 / this.scale;
        this.ctx.setLineDash([5 / this.scale, 5 / this.scale]);
        
        // Ligne de contrôle 1
        this.ctx.beginPath();
        this.ctx.moveTo(curve.debut.x, curve.debut.y);
        this.ctx.lineTo(curve.controle1.x, curve.controle1.y);
        this.ctx.stroke();
        
        // Ligne de contrôle 2
        this.ctx.beginPath();
        this.ctx.moveTo(curve.fin.x, curve.fin.y);
        this.ctx.lineTo(curve.controle2.x, curve.controle2.y);
        this.ctx.stroke();
        
        this.ctx.setLineDash([]);
      }
    });
  }

  /**
   * Dessine un point de contrôle
   */
  private dessinerPointControle(point: Point2D): void {
    this.ctx.fillStyle = this.CONTROL_POINT_COLOR;
    this.ctx.beginPath();
    this.ctx.arc(point.x, point.y, 3 / this.scale, 0, Math.PI * 2);
    this.ctx.fill();
  }

  /**
   * Dessine les éléments en cours de création
   */
  private dessinerElementsEnCours(): void {
    // Chemin de crayon en cours
    if (this.isDrawing && this.currentPath.length > 1) {
      this.ctx.strokeStyle = this.CRAYON_COLOR;
      this.ctx.lineWidth = 2 / this.scale;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.globalAlpha = 0.8;
      
      this.ctx.beginPath();
      this.ctx.moveTo(this.currentPath[0].x, this.currentPath[0].y);
      
      for (let i = 1; i < this.currentPath.length; i++) {
        this.ctx.lineTo(this.currentPath[i].x, this.currentPath[i].y);
      }
      
      this.ctx.stroke();
      this.ctx.globalAlpha = 1;
    }

    // Ligne droite en cours
    if (this.ligneEnCours) {
      this.ctx.strokeStyle = this.LIGNE_COLOR;
      this.ctx.lineWidth = 2 / this.scale;
      this.ctx.globalAlpha = 0.7;
      this.ctx.setLineDash([5 / this.scale, 5 / this.scale]);
      
      this.ctx.beginPath();
      this.ctx.moveTo(this.ligneEnCours.debut.x, this.ligneEnCours.debut.y);
      this.ctx.lineTo(this.ligneEnCours.fin.x, this.ligneEnCours.fin.y);
      this.ctx.stroke();
      
      this.ctx.setLineDash([]);
      this.ctx.globalAlpha = 1;
    }

    // Courbe de Bézier en cours
    if (this.bezierEnCours) {
      this.ctx.strokeStyle = this.BEZIER_COLOR;
      this.ctx.lineWidth = 2 / this.scale;
      this.ctx.globalAlpha = 0.7;
      
      if (this.etapeBezier >= 2) {
        this.ctx.beginPath();
        this.ctx.moveTo(this.bezierEnCours.debut.x, this.bezierEnCours.debut.y);
        this.ctx.bezierCurveTo(
          this.bezierEnCours.controle1.x, this.bezierEnCours.controle1.y,
          this.bezierEnCours.controle2.x, this.bezierEnCours.controle2.y,
          this.bezierEnCours.fin.x, this.bezierEnCours.fin.y
        );
        this.ctx.stroke();
        
        // Lignes de contrôle
        this.ctx.strokeStyle = this.CONTROL_POINT_COLOR;
        this.ctx.lineWidth = 1 / this.scale;
        this.ctx.setLineDash([3 / this.scale, 3 / this.scale]);
        
        this.ctx.beginPath();
        this.ctx.moveTo(this.bezierEnCours.debut.x, this.bezierEnCours.debut.y);
        this.ctx.lineTo(this.bezierEnCours.controle1.x, this.bezierEnCours.controle1.y);
        this.ctx.stroke();
        
        this.ctx.beginPath();
        this.ctx.moveTo(this.bezierEnCours.fin.x, this.bezierEnCours.fin.y);
        this.ctx.lineTo(this.bezierEnCours.controle2.x, this.bezierEnCours.controle2.y);
        this.ctx.stroke();
        
        this.ctx.setLineDash([]);
      } else if (this.etapeBezier === 1) {
        // Juste une ligne droite pour l'instant
        this.ctx.setLineDash([5 / this.scale, 5 / this.scale]);
        this.ctx.beginPath();
        this.ctx.moveTo(this.bezierEnCours.debut.x, this.bezierEnCours.debut.y);
        this.ctx.lineTo(this.bezierEnCours.fin.x, this.bezierEnCours.fin.y);
        this.ctx.stroke();
        this.ctx.setLineDash([]);
      }
      
      this.ctx.globalAlpha = 1;
    }
  }

  /**
   * Dessine les poignées de sélection
   */
  private dessinerPoigneesSelection(): void {
    const handleSize = this.HANDLE_SIZE / this.scale;
    
    // Poignées pour les lignes droites sélectionnées
    this.lignesDroites.forEach(ligne => {
      if (ligne.selected) {
        this.dessinerPoignee(ligne.debut);
        this.dessinerPoignee(ligne.fin);
      }
    });
    
    // Poignées pour les courbes de Bézier sélectionnées
    this.bezierCurves.forEach(curve => {
      if (curve.selected) {
        this.dessinerPoignee(curve.debut);
        this.dessinerPoignee(curve.fin);
        this.dessinerPoigneeControle(curve.controle1);
        this.dessinerPoigneeControle(curve.controle2);
      }
    });
  }

  /**
   * Dessine une poignée de sélection
   */
  private dessinerPoignee(point: Point2D): void {
    const handleSize = this.HANDLE_SIZE / this.scale;
    
    this.ctx.fillStyle = this.HANDLE_COLOR;
    this.ctx.strokeStyle = '#ffffff';
    this.ctx.lineWidth = 1 / this.scale;
    
    this.ctx.beginPath();
    this.ctx.rect(
      point.x - handleSize / 2,
      point.y - handleSize / 2,
      handleSize,
      handleSize
    );
    this.ctx.fill();
    this.ctx.stroke();
  }

  /**
   * Dessine une poignée de contrôle
   */
  private dessinerPoigneeControle(point: Point2D): void {
    const handleSize = this.HANDLE_SIZE / this.scale;
    
    this.ctx.fillStyle = this.CONTROL_POINT_COLOR;
    this.ctx.strokeStyle = '#ffffff';
    this.ctx.lineWidth = 1 / this.scale;
    
    this.ctx.beginPath();
    this.ctx.arc(point.x, point.y, handleSize / 2, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.stroke();
  }

  /**
   * Dessine le chemin en cours de création
   */
  private dessinerCheminEnCours(): void {
    this.ctx.strokeStyle = this.CRAYON_COLOR;
    this.ctx.lineWidth = 2 / this.scale;
    this.ctx.globalAlpha = 0.7;
    
    this.ctx.beginPath();
    this.ctx.moveTo(this.currentPath[0].x, this.currentPath[0].y);
    
    for (let i = 1; i < this.currentPath.length; i++) {
      this.ctx.lineTo(this.currentPath[i].x, this.currentPath[i].y);
    }
    
    this.ctx.stroke();
    this.ctx.globalAlpha = 1;
  }

  /**
   * Ajoute une courbe de Bézier
   */
  public ajouterCourbeBezier(courbe: CourbeBezier): void {
    this.bezierCurves.push(courbe);
    this.redessiner();
  }

  /**
   * Efface tout le dessin
   */
  public effacer(): void {
    this.paths = [];
    this.lignesDroites = [];
    this.bezierCurves = [];
    this.currentPath = [];
    this.selectedPathIndex = -1;
    this.deselectionnerTout();
    this.redessiner();
  }

  /**
   * Annule la dernière action
   */
  public annuler(): void {
    if (this.paths.length > 0) {
      this.paths.pop();
    } else if (this.lignesDroites.length > 0) {
      this.lignesDroites.pop();
    } else if (this.bezierCurves.length > 0) {
      this.bezierCurves.pop();
    }
    this.deselectionnerTout();
    this.redessiner();
  }

  /**
   * Change l'outil de dessin
   */
  public changerOutil(outil: OutilDessin): void {
    this.currentTool = outil;
    this.deselectionnerTout();
    this.ligneEnCours = null;
    this.bezierEnCours = null;
    this.etapeBezier = 0;
    this.redessiner();
  }

  /**
   * Obtient l'outil actuel
   */
  public getOutilActuel(): OutilDessin {
    return this.currentTool;
  }

  /**
   * Supprime les éléments sélectionnés
   */
  public supprimerSelection(): void {
    this.lignesDroites = this.lignesDroites.filter(ligne => !ligne.selected);
    this.bezierCurves = this.bezierCurves.filter(curve => !curve.selected);
    
    // Supprimer le chemin de crayon sélectionné
    if (this.selectedPathIndex !== -1) {
      this.paths.splice(this.selectedPathIndex, 1);
      this.selectedPathIndex = -1;
    }
    
    this.redessiner();
  }

  /**
   * Active ou désactive l'accroche magnétique
   */
  public toggleAccroche(): void {
    this.accrocheActive = !this.accrocheActive;
    this.redessiner();
  }

  /**
   * Obtient l'état de l'accroche magnétique
   */
  public getAccrocheActive(): boolean {
    return this.accrocheActive;
  }

  /**
   * Définit le rayon d'accroche magnétique
   */
  public changerRayonAccroche(rayon: number): void {
    this.rayonAccroche = Math.max(5, Math.min(50, rayon));
    this.redessiner();
  }

  /**
   * Obtient le rayon d'accroche actuel
   */
  public getRayonAccroche(): number {
    return this.rayonAccroche;
  }

  /**
   * Active/désactive la grille
   */
  public toggleGrille(): void {
    this.showGrid = !this.showGrid;
    this.redessiner();
  }

  /**
   * Change la taille de la grille
   */
  public changerTailleGrille(taille: number): void {
    this.gridSize = Math.max(5, Math.min(50, taille));
    this.redessiner();
  }

  /**
   * Recentre la vue
   */
  public recentrer(): void {
    this.scale = 1;
    this.offset = { x: 0, y: 0 };
    this.redessiner();
  }

  /**
   * Exporte le motif personnalisé
   */
  public exporterMotif(nom: string): MotifPersonnalise {
    return {
      id: `motif_${Date.now()}`,
      nom,
      courbes: [...this.bezierCurves],
      lignes: [...this.paths],
      lignesDroites: [...this.lignesDroites],
      largeur: this.canvas.width,
      hauteur: this.canvas.height
    };
  }

  /**
   * Importe un motif personnalisé
   */
  public importerMotif(motif: MotifPersonnalise): void {
    this.effacer();
    this.bezierCurves = [...motif.courbes];
    this.paths = [...motif.lignes];
    this.lignesDroites = motif.lignesDroites ? [...motif.lignesDroites] : [];
    this.redessiner();
  }

  /**
   * Redimensionne le canvas
   */
  public redimensionner(largeur: number, hauteur: number): void {
    this.canvas.width = largeur;
    this.canvas.height = hauteur;
    this.redessiner();
  }

  /**
   * Génère une miniature du motif sous forme de data URL
   */
  public static genererMiniature(motif: MotifPersonnalise, taille: number = 100): string {
    // Créer un canvas temporaire pour la miniature
    const canvas = document.createElement('canvas');
    canvas.width = taille;
    canvas.height = taille;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return '';
    
    // Fond blanc
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, taille, taille);
    
    // Calculer les limites du motif
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    
    // Traiter les lignes de crayon
    motif.lignes.forEach(ligne => {
      ligne.forEach(point => {
        minX = Math.min(minX, point.x);
        maxX = Math.max(maxX, point.x);
        minY = Math.min(minY, point.y);
        maxY = Math.max(maxY, point.y);
      });
    });
    
    // Traiter les lignes droites
    if (motif.lignesDroites) {
      motif.lignesDroites.forEach(ligne => {
        minX = Math.min(minX, ligne.debut.x, ligne.fin.x);
        maxX = Math.max(maxX, ligne.debut.x, ligne.fin.x);
        minY = Math.min(minY, ligne.debut.y, ligne.fin.y);
        maxY = Math.max(maxY, ligne.debut.y, ligne.fin.y);
      });
    }
    
    // Traiter les courbes de Bézier
    motif.courbes.forEach(courbe => {
      minX = Math.min(minX, courbe.debut.x, courbe.fin.x, courbe.controle1.x, courbe.controle2.x);
      maxX = Math.max(maxX, courbe.debut.x, courbe.fin.x, courbe.controle1.x, courbe.controle2.x);
      minY = Math.min(minY, courbe.debut.y, courbe.fin.y, courbe.controle1.y, courbe.controle2.y);
      maxY = Math.max(maxY, courbe.debut.y, courbe.fin.y, courbe.controle1.y, courbe.controle2.y);
    });
    
    // Si pas de points, retourner un canvas vide
    if (minX === Infinity) return canvas.toDataURL();
    
    // Calculer l'échelle pour ajuster le motif dans la miniature
    const motifWidth = maxX - minX;
    const motifHeight = maxY - minY;
    const padding = 10;
    const availableSize = taille - 2 * padding;
    
    const scaleX = motifWidth > 0 ? availableSize / motifWidth : 1;
    const scaleY = motifHeight > 0 ? availableSize / motifHeight : 1;
    const scale = Math.min(scaleX, scaleY);
    
    // Centrer le motif
    const offsetX = padding + (availableSize - motifWidth * scale) / 2 - minX * scale;
    const offsetY = padding + (availableSize - motifHeight * scale) / 2 - minY * scale;
    
    // Dessiner les lignes
    ctx.strokeStyle = '#2196F3';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    motif.lignes.forEach(ligne => {
      if (ligne.length > 1) {
        ctx.beginPath();
        const firstPoint = ligne[0];
        ctx.moveTo(
          firstPoint.x * scale + offsetX,
          firstPoint.y * scale + offsetY
        );
        
        for (let i = 1; i < ligne.length; i++) {
          const point = ligne[i];
          ctx.lineTo(
            point.x * scale + offsetX,
            point.y * scale + offsetY
          );
        }
        
        ctx.stroke();
      }
    });
    
    // Dessiner les courbes de Bézier si présentes
    if (motif.courbes && motif.courbes.length > 0) {
      ctx.strokeStyle = '#FF5722';
      motif.courbes.forEach(courbe => {
        ctx.beginPath();
        ctx.moveTo(
          courbe.debut.x * scale + offsetX,
          courbe.debut.y * scale + offsetY
        );
        ctx.bezierCurveTo(
          courbe.controle1.x * scale + offsetX,
          courbe.controle1.y * scale + offsetY,
          courbe.controle2.x * scale + offsetX,
          courbe.controle2.y * scale + offsetY,
          courbe.fin.x * scale + offsetX,
          courbe.fin.y * scale + offsetY
        );
        ctx.stroke();
      });
    }
    
    // Dessiner les lignes droites si présentes
    if (motif.lignesDroites && motif.lignesDroites.length > 0) {
      ctx.strokeStyle = '#4CAF50';
      motif.lignesDroites.forEach(ligne => {
        ctx.beginPath();
        ctx.moveTo(
          ligne.debut.x * scale + offsetX,
          ligne.debut.y * scale + offsetY
        );
        ctx.lineTo(
          ligne.fin.x * scale + offsetX,
          ligne.fin.y * scale + offsetY
        );
        ctx.stroke();
      });
    }
    
    return canvas.toDataURL();
  }

  /**
   * Dessine les indicateurs d'accroche magnétique
   */
  private dessinerIndicateursAccroche(): void {
    if (!this.accrocheActive) return;
    
    // Dessiner les points d'accroche disponibles
    this.ctx.fillStyle = this.ACCROCHE_COLOR;
    this.ctx.strokeStyle = '#ffffff';
    this.ctx.lineWidth = 1 / this.scale;
    
    const accrocheSize = this.ACCROCHE_SIZE / this.scale;
    
    // Points des chemins de crayon (premier et dernier point de chaque chemin)
    this.paths.forEach(path => {
      if (path.length > 0) {
        // Premier point du chemin
        this.ctx.beginPath();
        this.ctx.arc(path[0].x, path[0].y, accrocheSize / 2, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.stroke();
        
        // Dernier point du chemin (si différent du premier)
        if (path.length > 1) {
          const dernierPoint = path[path.length - 1];
          this.ctx.beginPath();
          this.ctx.arc(dernierPoint.x, dernierPoint.y, accrocheSize / 2, 0, Math.PI * 2);
          this.ctx.fill();
          this.ctx.stroke();
        }
      }
    });
    
    // Points des lignes droites
    this.lignesDroites.forEach(ligne => {
      this.ctx.beginPath();
      this.ctx.arc(ligne.debut.x, ligne.debut.y, accrocheSize / 2, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.stroke();
      
      this.ctx.beginPath();
      this.ctx.arc(ligne.fin.x, ligne.fin.y, accrocheSize / 2, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.stroke();
    });
    
    // Points des courbes de Bézier
    this.bezierCurves.forEach(courbe => {
      const points = [courbe.debut, courbe.fin, courbe.controle1, courbe.controle2];
      
      points.forEach(point => {
        this.ctx.beginPath();
        this.ctx.arc(point.x, point.y, accrocheSize / 2, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.stroke();
      });
    });
  }



  /**
   * Dispose des ressources
   */
  public dispose(): void {
    // Supprimer les événements
    this.canvas.removeEventListener('mousedown', this.onMouseDown.bind(this));
    this.canvas.removeEventListener('mousemove', this.onMouseMove.bind(this));
    this.canvas.removeEventListener('mouseup', this.onMouseUp.bind(this));
    this.canvas.removeEventListener('wheel', this.onWheel.bind(this));
    this.canvas.removeEventListener('touchstart', this.onTouchStart.bind(this));
    this.canvas.removeEventListener('touchmove', this.onTouchMove.bind(this));
    this.canvas.removeEventListener('touchend', this.onTouchEnd.bind(this));
    this.canvas.removeEventListener('keydown', this.onKeyDown.bind(this));
  }
}